import { AlertTriangle, Sparkles, ArrowRight, ExternalLink } from 'lucide-react'
import { GRADE_COLORS } from '@/lib/radar/scoring'
import { sanitizeHref } from '@/lib/sanitize'
import type { PersistedHallucinationCheckResult } from '@/lib/tools/hallucination-check'
import { MiniStat } from './MiniStat'
import type { ToolDetailProps } from './detailRegistry'

interface HallucinationCheckViewData {
  grade: string | null
  overallScore: number | null
  scoreStatus?: 'graded' | 'withheld'
  judgedProviders?: string[]
  indeterminateReasons?: string[]
  providerCounts?: {
    available: number
    responded: number
    judged: number
    indeterminate: number
    affected: number
  }
  providerDetails?: {
    provider: string
    evaluationStatus: 'judged' | 'indeterminate'
    indeterminateReason?: string
    reportOnly?: boolean
  }[]
  url?: string
  aiSummary?: { summary?: string; actions?: string[] } | null
  scoreBreakdown?: {
    category: string
    score: number
    maxScore: number
    details?: string
    color?: string
  }[]
  bySeverity?: { high: number; medium: number; low: number }
  flags?: {
    severity: string
    provider: string
    category: string
    claim: string
    reality: string
  }[]
  providersChecked?: unknown[]
  recommendations?: { priority: string; message: string }[]
}

/**
 * Dashboard detail renderer for `hallucinationCheck` (Phase 3, Step 4 — Class D,
 * dashboard-only, last of the dashboard-only block). Faithfully reproduces the
 * legacy ToolDetailPanel rendering: header (icon + name + grade pill + url), the
 * AI Analysis block, score-breakdown bars, the bySeverity MiniStats
 * (High/Medium/Low), the Detected Inaccuracies flag cards (AI-claim vs reality,
 * two-line styling), the empty green "no inaccuracies" state (only when `flags`
 * is an empty array), and recommendations.
 *
 * `providerDetails` / `groundTruth` / `byCategory` / `totalInaccuracies` are
 * carried in the data but were never rendered by the legacy panel — left
 * unrendered to preserve exact parity. Grade comes from `data.grade` — never
 * recomputed. Render-layer only.
 */
export function HallucinationCheckDetail({
  data,
  toolIcon: Icon,
  toolColor,
  toolName,
}: ToolDetailProps) {
  const d = data as unknown as HallucinationCheckViewData
  const persisted = d as unknown as PersistedHallucinationCheckResult
  const withheld =
    persisted.scoreStatus === 'withheld' ||
    persisted.overallScore == null ||
    persisted.grade == null
  const scoreColor = withheld
    ? 'var(--warning-text)'
    : GRADE_COLORS[persisted.grade!]
  const hasJudgedProviderCount = Array.isArray(persisted.judgedProviders)
  const providerCount = hasJudgedProviderCount
    ? persisted.judgedProviders!.length
    : persisted.providersChecked?.length || 0
  const providerCountLabel = hasJudgedProviderCount
    ? 'judged AI providers'
    : 'AI providers checked under the legacy method'
  const entityMismatchCount =
    d.providerDetails?.filter(
      detail =>
        !detail.reportOnly && detail.indeterminateReason === 'entity_mismatch'
    ).length ?? 0
  const excludedProviderCount =
    !withheld && d.providerCounts
      ? Math.max(0, d.providerCounts.available - d.providerCounts.judged)
      : 0

  return (
    <div
      id='tool-detail-panel'
      className='rounded-xl border border-border bg-card p-5 space-y-5'
    >
      {/* Header */}
      <div className='flex items-baseline gap-3'>
        <span className='relative top-[3px] flex-shrink-0'>
          <Icon size={22} style={{ color: toolColor }} />
        </span>
        <h3 className='text-base font-bold text-foreground leading-none'>
          {toolName}
        </h3>
        <span
          className='text-sm font-bold px-2.5 py-1 rounded leading-none'
          style={{
            background: withheld
              ? 'color-mix(in srgb, var(--warning-text) 12%, transparent)'
              : `${scoreColor}15`,
            color: scoreColor,
          }}
        >
          {withheld ? 'Score withheld' : `${persisted.overallScore}/100`}
        </span>
        {d.url &&
          (() => {
            const safeUrl = sanitizeHref(d.url, {
              allowedSchemes: ['https:', 'http:'],
            })
            if (!safeUrl) return null
            return (
              <a
                href={safeUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='ml-auto flex items-center gap-1 text-xs hover:underline text-muted-foreground'
              >
                {d.url} <ExternalLink size={9} />
              </a>
            )
          })()}
      </div>

      {excludedProviderCount > 0 && (
        <div
          className='border px-4 py-3 text-xs leading-relaxed text-foreground/80'
          style={{
            borderColor:
              'color-mix(in srgb, var(--warning-text) 12%, transparent)',
            backgroundColor:
              'color-mix(in srgb, var(--warning-text) 6%, transparent)',
          }}
        >
          <strong style={{ color: 'var(--warning-text)' }}>
            Partial factual evaluation:
          </strong>{' '}
          {d.providerCounts!.judged} of {d.providerCounts!.available} scored
          provider responses were fact-checked. {excludedProviderCount}{' '}
          {excludedProviderCount === 1 ? 'response was' : 'responses were'}{' '}
          excluded and did not affect this score.
          {entityMismatchCount > 0 && (
            <>
              {' '}
              {entityMismatchCount === 1
                ? 'One excluded response described a different same-named entity'
                : `${entityMismatchCount} excluded responses described different same-named entities`}
              ; that risk is measured under Brand Disambiguation.
            </>
          )}
        </div>
      )}

      {/* AI Summary */}
      {d.aiSummary?.summary && (
        <div
          className='rounded-lg border p-5'
          style={{ borderColor: '#008dcc20', background: '#008dcc08' }}
        >
          <div className='flex items-center gap-2 mb-3'>
            <Sparkles size={14} style={{ color: '#008dcc' }} />
            <span
              className='text-xs font-bold uppercase tracking-wide'
              style={{ color: '#008dcc' }}
            >
              AI Analysis
            </span>
          </div>
          <p className='text-sm leading-relaxed text-foreground/90'>
            {d.aiSummary.summary}
          </p>
          {d.aiSummary.actions && d.aiSummary.actions.length > 0 && (
            <ul className='mt-4 space-y-2'>
              {d.aiSummary.actions.map((a, i) => (
                <li
                  key={i}
                  className='flex items-start gap-2 text-sm text-foreground/80'
                >
                  <ArrowRight
                    size={14}
                    className='mt-0.5 flex-shrink-0 text-primary'
                  />
                  {a}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Score breakdown */}
      {!withheld && d.scoreBreakdown && d.scoreBreakdown.length > 0 && (
        <div>
          <h4 className='text-xs font-bold mb-4 uppercase tracking-wide text-muted-foreground'>
            Score Breakdown
          </h4>
          <div className='space-y-4'>
            {d.scoreBreakdown.map(cat => (
              <div key={cat.category}>
                <div className='flex justify-between text-sm mb-1.5'>
                  <span className='text-foreground/90 font-medium'>
                    {cat.category}
                  </span>
                  <span className='font-mono font-bold text-foreground'>
                    {cat.score}/{cat.maxScore}
                  </span>
                </div>
                <div className='h-2 rounded-full bg-muted'>
                  <div
                    className='h-full rounded-full transition-all duration-500'
                    style={{
                      width: `${cat.maxScore > 0 ? (cat.score / cat.maxScore) * 100 : 0}%`,
                      background: cat.color || toolColor,
                    }}
                  />
                </div>
                {cat.details && (
                  <p className='text-xs mt-1.5 text-muted-foreground leading-relaxed'>
                    {cat.details}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hallucination Check: flags */}
      <div>
        <div className='grid grid-cols-3 gap-3 mb-4' hidden={withheld}>
          <MiniStat
            label='High Severity'
            value={d.bySeverity?.high || 0}
            color='#F90B8A'
          />
          <MiniStat
            label='Medium'
            value={d.bySeverity?.medium || 0}
            color='var(--warning-text)'
          />
          <MiniStat
            label='Low'
            value={d.bySeverity?.low || 0}
            color='#22c55e'
          />
        </div>
        {!withheld && d.flags && d.flags.length > 0 && (
          <div>
            <h4 className='text-xs font-bold mb-3 uppercase tracking-wide text-muted-foreground'>
              Detected Inaccuracies ({d.flags.length})
            </h4>
            <div className='space-y-3'>
              {d.flags.map((flag, i) => (
                <div
                  key={i}
                  className='rounded-lg border border-border bg-background p-4'
                >
                  <div className='flex items-center gap-2 mb-2'>
                    <span
                      className='text-xs px-2 py-0.5 rounded font-semibold uppercase'
                      style={{
                        background:
                          flag.severity === 'high'
                            ? '#F90B8A15'
                            : flag.severity === 'medium'
                              ? '#FDC30415'
                              : '#22c55e15',
                        color:
                          flag.severity === 'high'
                            ? '#F90B8A'
                            : flag.severity === 'medium'
                              ? 'var(--warning-text)'
                              : '#22c55e',
                      }}
                    >
                      {flag.severity}
                    </span>
                    <span className='text-xs font-medium text-muted-foreground'>
                      {flag.provider} / {flag.category}
                    </span>
                  </div>
                  <p
                    className='text-sm leading-relaxed mb-1'
                    style={{ color: '#F90B8A' }}
                  >
                    <span className='font-bold'>AI claims:</span> &ldquo;
                    {flag.claim}&rdquo;
                  </p>
                  <p
                    className='text-sm leading-relaxed'
                    style={{ color: '#22c55e' }}
                  >
                    <span className='font-bold'>Your site says:</span> &ldquo;
                    {flag.reality}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        {!withheld && d.flags && d.flags.length === 0 && (
          <p
            className='text-sm text-center py-6 font-medium'
            style={{ color: '#22c55e' }}
          >
            No supported factual inaccuracies detected across {providerCount}{' '}
            {providerCountLabel}.
          </p>
        )}
      </div>

      {withheld && (
        <div className='rounded-lg border border-border bg-muted/30 p-5'>
          <div className='flex items-center gap-2 mb-2 text-[var(--warning-text)]'>
            <AlertTriangle size={16} />
            <span className='text-xs font-bold uppercase tracking-wide'>
              Evaluation incomplete
            </span>
          </div>
          <p className='text-sm leading-relaxed text-foreground/85'>
            No Hallucination Check score was issued because no AI provider
            response could be evaluated against verified website evidence.
          </p>
        </div>
      )}

      {/* Recommendations */}
      {!withheld && d.recommendations && d.recommendations.length > 0 && (
        <div>
          <h4 className='text-xs font-bold mb-3 uppercase tracking-wide text-muted-foreground'>
            Recommendations ({d.recommendations.length})
          </h4>
          <div className='space-y-2.5'>
            {d.recommendations.map((rec, i) => (
              <div
                key={i}
                className='flex items-start gap-3 text-sm leading-relaxed'
              >
                <span
                  className='w-2 h-2 rounded-full mt-1.5 flex-shrink-0'
                  style={{
                    background:
                      rec.priority === 'high'
                        ? '#F90B8A'
                        : rec.priority === 'medium'
                          ? 'var(--warning-text)'
                          : '#22c55e',
                  }}
                />
                <span className='text-foreground/85'>{rec.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
